<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
    <div
      v-for="field in fields"
      :key="field.name"
      :class="fieldColClass(field)"
    >
      <text-input
        v-if="field.type === 'text'"
        :model-value="getFieldValue(field.name)"
        :errors="fieldErrors(field.name)"
        :label="field.label"
        :required="field.required"
        @update:model-value="setFieldValue(field.name, $event)"
      />
      <page-link-select
        v-else-if="field.type === 'page_link'"
        :model-value="getFieldValue(field.name)"
        :options="pageOptions"
        :errors="fieldErrors(field.name)"
        :label="field.label"
        :required="field.required"
        @update:model-value="setPageLinkValue(field.name, $event)"
      />
      <resource-link-select
        v-else-if="field.type === 'resource_link'"
        :model-value="getFieldValue(field.name) || ''"
        :errors="fieldErrors(field.name)"
        :label="field.label"
        :required="field.required"
        include-articles
        include-pages
        placeholder="Wybierz specjalistę, specjalizację, usługę, aktualność lub stronę…"
        @update:model-value="setFieldValue(field.name, $event)"
      />
      <p
        v-if="field.type === 'page_link' && pageOptions.length === 0"
        class="mt-1 text-xs text-red-600"
      >
        Brak stron w katalogu.
        <a :href="$routes.admin_navigation_pages()" class="underline">Przeglądaj strony nawigacji</a>
      </p>
      <p
        v-else-if="field.type === 'page_link' || field.type === 'resource_link'"
        class="mt-1 text-xs text-gray-500"
      >
        Wspólny dla wersji PL i EN. Wybór na jednej karcie zapisuje się w obu językach.
      </p>
      <textarea-input
        v-else-if="field.type === 'textarea'"
        :model-value="getFieldValue(field.name)"
        :errors="fieldErrors(field.name)"
        :label="field.label"
        :required="field.required"
        @update:model-value="setFieldValue(field.name, $event)"
      />
      <div v-else-if="field.type === 'rich_text'" class="md:col-span-2">
        <label class="form-label">
          {{ field.label }}<span v-if="field.required" class="text-red-500"> *</span>
        </label>
        <tiptap-editor
          :model-value="getFieldValue(field.name) || ''"
          :placeholder="field.placeholder || 'Treść...'"
          @update:model-value="setFieldValue(field.name, $event)"
        />
        <div v-if="fieldErrors(field.name).length" class="form-error">
          {{ fieldErrors(field.name)[0] }}
        </div>
      </div>
      <file-input
        v-else-if="field.type === 'image'"
        :model-value="form.images[field.name]"
        :existing-url="existingImageUrl(field.name)"
        :errors="form.errors[`images.${field.name}`] || form.errors.images"
        :label="field.label"
        :required="field.required"
        :accept="field.accept?.join(',')"
        @update:model-value="(value) => { form.images[field.name] = value; }"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import TextInput from '@/Shared/TextInput.vue';
import TextareaInput from '@/Shared/TextareaInput.vue';
import PageLinkSelect from '@/Shared/PageLinkSelect.vue';
import ResourceLinkSelect from '@/Shared/ResourceLinkSelect.vue';
import FileInput from '@/Shared/FileInput.vue';
import TiptapEditor from '@/Shared/TiptapEditor.vue';

const inertiaPage = usePage();

const props = defineProps({
  fields: {
    type: Array,
    required: true,
  },
  form: {
    type: Object,
    required: true,
  },
  editingLocale: {
    type: String,
    required: true,
  },
  linkablePages: {
    type: Array,
    default: () => [],
  },
  getFieldValue: {
    type: Function,
    required: true,
  },
  setFieldValue: {
    type: Function,
    required: true,
  },
  existingImages: {
    type: Object,
    default: () => ({}),
  },
});

const pageOptions = computed(() => {
  const fromProps = props.linkablePages || [];
  if (fromProps.length > 0) return fromProps;
  return inertiaPage.props.navigation_link_pages || inertiaPage.props.navigationLinkPages || [];
});

const fieldColClass = (field) => {
  if (field.type === 'textarea' || field.type === 'rich_text' || field.type === 'page_link' || field.type === 'resource_link') {
    return 'md:col-span-2';
  }
  return '';
};

const fieldErrors = (fieldName) =>
  props.form.errors[`data_${props.editingLocale}.${fieldName}`]
  || props.form.errors[`data.${fieldName}`]
  || [];

const setPageLinkValue = (fieldName, value) => {
  if (value === '' || value === null || value === undefined) {
    props.setFieldValue(fieldName, null);
    return;
  }
  props.setFieldValue(fieldName, Number(value));
};

const existingImageUrl = (fieldName) => {
  const imageValue = props.existingImages?.[fieldName];
  if (!imageValue) return null;
  return typeof imageValue === 'string' ? imageValue : imageValue.url || null;
};
</script>
